import { memo, type CSSProperties } from 'react';
import { useMantineColorScheme, useMantineTheme } from '@mantine/core';
import { useTranslation } from 'react-i18next';
import { buildMarketingUrl } from '@/constants';
import styles from './BrandLogo.module.css';

interface BrandLogoProps {
  showLabel?: boolean;
  size?: 'sm' | 'lg';
  /** When set, the icon is centered inside a lead cell of this width
   *  (used to align the brand mark over the sidebar rail). */
  iconLeadWidth?: string;
  /** Show the tagline under the name. Defaults to true only for size 'lg'. */
  showTagline?: boolean;
  /** Stack the label beneath the icon instead of beside it (narrow sidebar rail). */
  orientation?: 'horizontal' | 'vertical';
  /** Wrap the logo in a link to the marketing site (opens in a new tab).
   *  Used on the split-screen / gate screens; off inside the app chrome. */
  linkToHome?: boolean;
}

export const BrandLogo = memo(function BrandLogo({
  showLabel = true,
  size = 'lg',
  iconLeadWidth,
  showTagline,
  orientation = 'horizontal',
  linkToHome = false,
}: BrandLogoProps) {
  const { colorScheme } = useMantineColorScheme();
  const theme = useMantineTheme();
  const { t, i18n } = useTranslation();
  const isDark = colorScheme === 'dark';

  const gridStroke = isDark ? theme.white : theme.colors.gray[5];
  const pinColor = isDark ? theme.colors.orange[6] : theme.colors.indigo[6];

  const svgSize =
    size === 'lg' ? { width: 42, height: 39 } : { width: 30, height: 28 };

  const withTagline = showTagline ?? size === 'lg';
  // A tagline at the small size is a compact brand lockup (e.g. the TopBar):
  // tighter icon↔text gap and smaller name/tagline type.
  const isCompactLockup = withTagline && size !== 'lg';

  const wrapperClass = [
    styles.wrapper,
    size === 'lg' ? styles.wrapperLg : '',
    isCompactLockup ? styles.compact : '',
    orientation === 'vertical' ? styles.vertical : '',
  ]
    .filter(Boolean)
    .join(' ');

  const iconSvg = (
    <svg
      width={svgSize.width}
      height={svgSize.height}
      viewBox="0 0 300 280"
      fill="none"
      aria-hidden="true"
    >
      <g
        stroke={gridStroke}
        strokeWidth="5"
        strokeLinecap="butt"
        strokeLinejoin="miter"
      >
        <polygon points="80,155 220,155 280,260 20,260" />
        <line x1="65" y1="181" x2="235" y2="181" />
        <line x1="50" y1="207" x2="250" y2="207" />
        <line x1="35" y1="234" x2="265" y2="234" />
        <line x1="108" y1="155" x2="72" y2="260" />
        <line x1="136" y1="155" x2="124" y2="260" />
        <line x1="164" y1="155" x2="176" y2="260" />
        <line x1="192" y1="155" x2="228" y2="260" />
      </g>
      <path
        fillRule="evenodd"
        fill={pinColor}
        d="M150,215 C133,192 92,155 92,112 C92,80 118,55 150,55 C182,55 208,80 208,112 C208,155 167,192 150,215 Z M150,142 C132.3,142 118,127.7 118,110 C118,92.3 132.3,78 150,78 C167.7,78 182,92.3 182,110 C182,127.7 167.7,142 150,142 Z"
      />
      <circle cx="150" cy="110" r="17" fill={pinColor} />
    </svg>
  );

  const content = (
    <div className={wrapperClass}>
      {iconLeadWidth ? (
        <span
          className={styles.iconLead}
          style={
            {
              '--icon-lead-width': iconLeadWidth,
              '--icon-width': `${svgSize.width}px`,
            } as CSSProperties
          }
        >
          {iconSvg}
        </span>
      ) : (
        iconSvg
      )}
      {showLabel &&
        (withTagline ? (
          <div className={styles.labelGroup}>
            <span className={styles.label}>GeoMarkup</span>
            <span className={styles.tagline}>{t('app.tagline')}</span>
          </div>
        ) : (
          <span className={styles.label}>GeoMarkup</span>
        ))}
    </div>
  );

  if (!linkToHome) {
    return content;
  }

  return (
    <a
      href={buildMarketingUrl(i18n.language)}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={t('app.backToHome')}
      className={styles.link}
    >
      {content}
    </a>
  );
});
